/**
 * DashBookings — Phase 6
 * Business-side bookings management (accept / reject / reschedule / complete).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Calendar, Check, Clock, PhoneCall, RefreshCw, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "../../../lib/i18n";
import { commsAPI, BOOKING_KINDS, BOOKING_STATUS_LABELS } from "../../../lib/comms";

const STATUS_TABS = [
  { key: "",         ar: "الكل",      en: "All"        },
  { key: "pending",  ar: "معلّق",     en: "Pending"    },
  { key: "accepted", ar: "مقبول",     en: "Accepted"   },
  { key: "rescheduled", ar: "معاد جدولته", en: "Rescheduled" },
  { key: "completed", ar: "منجز",     en: "Completed"  },
  { key: "rejected", ar: "مرفوض",     en: "Rejected"   },
  { key: "canceled", ar: "ملغى",      en: "Canceled"   },
];

const TONE_CLASSES = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue:  "bg-blue-50 text-blue-700 border-blue-200",
  red:   "bg-red-50 text-red-700 border-red-200",
  gray:  "bg-neutral-100 text-neutral-700 border-neutral-200",
};

function kindLabel(k, lang) {
  const found = BOOKING_KINDS.find((x) => x.key === k);
  return found ? (lang === "ar" ? found.ar : found.en) : k;
}

export default function DashBookings({ businessId }) {
  const { lang } = useI18n();
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(null); // booking obj
  const [newTime, setNewTime] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await commsAPI.businessBookings(businessId, status ? { status, limit: 100 } : { limit: 100 });
      setData(d);
    } catch {
      toast.error(lang === "ar" ? "تعذّر تحميل الحجوزات" : "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [businessId, status, lang]);

  useEffect(() => { load(); }, [load]);

  const decide = async (b, action, extra = {}) => {
    try {
      await commsAPI.bookingDecision(businessId, b.id, action, extra);
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      await load();
    } catch (e) {
      toast.error(lang === "ar" ? "فشل التحديث" : "Failed");
    }
  };

  const submitReschedule = async () => {
    if (!newTime) return toast.error(lang === "ar" ? "أدخل الوقت الجديد" : "Enter new time");
    await decide(rescheduling, "reschedule", { new_time: new Date(newTime).toISOString(), reason });
    setRescheduling(null); setNewTime(""); setReason("");
  };

  return (
    <div className="space-y-5" data-testid="dash-bookings">
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            type="button"
            key={t.key || "all"}
            onClick={() => setStatus(t.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              status === t.key
                ? "bg-[#0a0a0a] text-[#f7eecd]"
                : "bg-white border border-[#0a0a0a]/10 text-[#525252] hover:text-[#0a0a0a]"
            }`}
            data-testid={`bookings-tab-${t.key || "all"}`}
          >
            {lang === "ar" ? t.ar : t.en}
          </button>
        ))}
        <button type="button" onClick={load}
                className="ms-auto inline-flex items-center gap-1.5 text-[12px] text-[#525252] hover:text-[#0a0a0a]"
                data-testid="bookings-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
          {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#8a8a8a]">…</div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 text-[#8a8a8a]"
             data-testid="bookings-empty">
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {lang === "ar" ? "لا توجد حجوزات" : "No bookings"}
        </div>
      ) : (
        <div className="grid gap-3">
          {data.items.map((b) => {
            const label = BOOKING_STATUS_LABELS[b.status] || { ar: b.status, en: b.status, tone: "gray" };
            return (
              <div key={b.id}
                   className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4 flex flex-wrap gap-3 items-start"
                   data-testid={`booking-${b.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="text-[13px] font-bold text-[#0a0a0a]">
                      {kindLabel(b.kind, lang)}
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${TONE_CLASSES[label.tone]}`}>
                      {lang === "ar" ? label.ar : label.en}
                    </span>
                  </div>
                  <div className="text-[12px] text-[#525252] flex flex-wrap gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(b.requested_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                    </span>
                    {b.customer_name && <span>👤 {b.customer_name}</span>}
                    {b.customer_phone && (
                      <a href={`tel:${b.customer_phone}`} className="inline-flex items-center gap-1 hover:text-[#0a0a0a]">
                        <PhoneCall className="w-3.5 h-3.5" /> {b.customer_phone}
                      </a>
                    )}
                  </div>
                  {b.notes && (
                    <div className="mt-2 text-[12px] text-[#525252] bg-[#faf9f5] rounded-lg px-3 py-1.5">
                      {b.notes}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {["pending", "rescheduled"].includes(b.status) && (
                    <button type="button" onClick={() => decide(b, "accept")}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid={`booking-accept-${b.id}`}>
                      <Check className="w-3.5 h-3.5" /> {lang === "ar" ? "قبول" : "Accept"}
                    </button>
                  )}
                  {["pending", "accepted", "rescheduled"].includes(b.status) && (
                    <button type="button" onClick={() => setRescheduling(b)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold bg-blue-600 text-white hover:bg-blue-700"
                            data-testid={`booking-reschedule-${b.id}`}>
                      <Clock className="w-3.5 h-3.5" /> {lang === "ar" ? "إعادة جدولة" : "Reschedule"}
                    </button>
                  )}
                  {b.status === "accepted" && (
                    <button type="button" onClick={() => decide(b, "complete")}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold bg-[#0a0a0a] text-[#f7eecd]"
                            data-testid={`booking-complete-${b.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {lang === "ar" ? "إنجاز" : "Complete"}
                    </button>
                  )}
                  {["pending", "accepted", "rescheduled"].includes(b.status) && (
                    <button type="button" onClick={() => decide(b, "reject", { reason: "" })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                            data-testid={`booking-reject-${b.id}`}>
                      <X className="w-3.5 h-3.5" /> {lang === "ar" ? "رفض" : "Reject"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reschedule modal */}
      {rescheduling && (
        <>
          <button type="button" aria-label="close" onClick={() => setRescheduling(null)}
                  className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-4 top-24 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md z-50 rounded-2xl bg-white shadow-2xl p-5"
               data-testid="reschedule-modal">
            <h3 className="text-lg font-bold mb-3">{lang === "ar" ? "إعادة جدولة الحجز" : "Reschedule booking"}</h3>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-[#8a6f2e] mb-1">
              {lang === "ar" ? "الوقت الجديد" : "New time"}
            </label>
            <input type="datetime-local" value={newTime} onChange={(e) => setNewTime(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm mb-3"
                   data-testid="reschedule-time" />
            <label className="block text-[11px] font-bold uppercase tracking-widest text-[#8a6f2e] mb-1">
              {lang === "ar" ? "سبب (اختياري)" : "Reason (optional)"}
            </label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setRescheduling(null)}
                      className="px-4 py-2 rounded-full text-sm text-[#525252]">
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button type="button" onClick={submitReschedule}
                      className="px-4 py-2 rounded-full text-sm font-bold text-white bg-blue-600"
                      data-testid="reschedule-submit">
                {lang === "ar" ? "حفظ" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
